/**
 * Lottery Service for user-modern — ใช้ API และ logic เดียวกับ user-classic (LottoPage)
 * ดึงรอบหวยวันนี้, group ตามประเภท, คำนวณเวลาเหลือ
 */
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'

/** ดึงรอบหวยทั้งวัน — tenant ใหญ่มี 280+ รอบ/วัน; API รองรับสูงสุด 2000 */
export const PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT = 1000

type ParsedResponseBody = {
  data: unknown
  text: string
}

async function parseResponseBody(response: Response): Promise<ParsedResponseBody> {
  const text = await response.text().catch(() => '')
  if (!text) {
    return { data: null, text: '' }
  }

  try {
    return {
      data: JSON.parse(text),
      text,
    }
  } catch {
    return {
      data: null,
      text,
    }
  }
}

function resolvePlaceBetErrorMessage(response: Response, body: ParsedResponseBody): string {
  const status = Number(response.status || 0)
  const payloadMessage =
    typeof (body.data as { message?: unknown } | null)?.message === 'string'
      ? String((body.data as { message?: string }).message || '').trim()
      : ''
  const rawText = String(body.text || '').trim()
  const contentType = String(response.headers.get('content-type') || '').toLowerCase()
  const looksLikeHtml = contentType.includes('text/html') || rawText.startsWith('<!doctype') || rawText.startsWith('<html')

  if ([502, 503, 504].includes(status) || looksLikeHtml) {
    return 'ระบบรับแทงขัดข้องชั่วคราว กรุณาลองใหม่อีกครั้ง'
  }

  return payloadMessage || `Failed to place bet (HTTP ${status || 'unknown'})`
}

const SLIP_FETCH_RETRY_STATUSES = [502, 503, 504]
const SLIP_FETCH_RETRY_DELAYS_MS = [400, 900, 1800]

function isRetryableSlipFetchStatus(status: number): boolean {
  return SLIP_FETCH_RETRY_STATUSES.includes(status)
}

function resolveSlipFetchErrorMessage(status: number): string {
  if ([502, 503, 504].includes(status)) {
    return 'ระบบโพยหวยไม่พร้อมชั่วคราว กรุณารอสักครู่แล้วกดโหลดใหม่'
  }
  if (status === 401 || status === 403) {
    return 'กรุณาเข้าสู่ระบบใหม่'
  }
  return `โหลดโพยไม่สำเร็จ (รหัส ${status || 'ไม่ทราบ'})`
}

async function delayMs(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

export interface LotteryRound {
  _id: string
  huayId: string
  huayName: string
  huayCode?: string
  huayImageURL?: string | null
  huayTypeId: string
  huayTypeName: string
  huayTypeCode?: string
  huayTypeIcon?: string | null
  roundNumber: string
  status:
    | 'pending'
    | 'open'
    | 'closed'
    | 'announced'
    | 'cancelled'
    | 'completed'
    | 'scheduled'
    | 'processing'
    | 'settling'
    | 'settled'
    | 'suspended'
  isOpen: boolean
  betStartTime: string
  betEndTime: string
  resultAnnounceTime: string
  drawDate: string
  drawTime: string
  yeekeeConfig?: {
    numberDigits?: number
  } | null
  [key: string]: unknown
}

export interface PublicHuayType {
  _id: string
  huayTypeId?: string
  name: string
  code?: string
  imageUrl?: string | null
  imageURL?: string | null
  configuration?: { isActive?: boolean }
}

export interface LotteryRoundsResponse {
  success: boolean
  data: LotteryRound[]
  error?: string
}

const CLOSED_FOR_BETTING_STATUSES = new Set([
  'closed',
  'cancelled',
  'announced',
  'completed',
  'processing',
  'settling',
  'settled',
  'suspended',
])

export function canRoundAcceptBet(round: Pick<LotteryRound, 'status' | 'betEndTime'>): boolean {
  const status = String(round.status || '').toLowerCase()
  if (CLOSED_FOR_BETTING_STATUSES.has(status)) {
    return false
  }

  const betEndTime = new Date(round.betEndTime).getTime()
  if (!Number.isFinite(betEndTime)) {
    return false
  }

  return betEndTime > Date.now()
}

/**
 * รอบที่ "ยังไม่เปิดรับแทง" — สถานะยังไม่ปิด แต่ยังไม่ถึงเวลาเปิด (betStartTime ในอนาคต)
 * ใช้แยกการ์ดให้ขึ้นสีเทาว่ารอเวลาเปิด แทนที่จะกดได้แล้วโดนปฏิเสธว่า "ปิดรับแทงแล้ว"
 */
export function isRoundCancelled(round: Pick<LotteryRound, 'status'>): boolean {
  return String(round.status || '').toLowerCase() === 'cancelled'
}

/** class การ์ด lobby เมื่อปิดรับ — ยกเลิกใช้ h_cancelled แยกจาก h_close */
export function getLottoRoundClosedCardClass(
  round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
  active: boolean,
): string {
  if (active) return ''
  return isRoundCancelled(round) ? 'h_cancelled' : 'h_close'
}

/** สีข้อความสถานะบนการ์ด lobby */
export function getLottoRoundStatusColorClass(
  round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
  active: boolean,
  notYetOpen: boolean,
  defaults: { active?: string; notYetOpen?: string; closed?: string } = {},
): string {
  if (isRoundCancelled(round)) return 'lobby-huay-status-cancelled'
  if (active) return defaults.active ?? 'c_quaternary'
  if (notYetOpen) return defaults.notYetOpen ?? 'c_tertiary'
  return defaults.closed ?? 'c_danger'
}

/** สีชื่อหวยบนหัวการ์ด lobby */
export function getLottoRoundTitleColorClass(
  active: boolean,
  activeTitleClass = 'c_secondary',
): string {
  return active ? activeTitleClass : 'c_tertiary'
}

/** พื้นหลังการ์ด lobby — ปิด/ยกเลิกใช้เทาเหมือนกัน แยกด้วยขอบ/ข้อความ */
export function getLottoRoundCardBgClass(
  _round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
  active: boolean,
  activeBgClass: string,
): string {
  return active ? activeBgClass : 'bg_gd_secondary'
}

export function isRoundNotYetOpen(round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>): boolean {
  const status = String(round.status || '').toLowerCase()
  if (CLOSED_FOR_BETTING_STATUSES.has(status)) {
    return false
  }

  const betStartTime = new Date(round.betStartTime).getTime()
  if (!Number.isFinite(betStartTime)) {
    return false
  }

  // ถ้าเลยเวลาปิดไปแล้ว ถือว่าปิด ไม่ใช่ "ยังไม่เปิด"
  const betEndTime = new Date(round.betEndTime).getTime()
  if (Number.isFinite(betEndTime) && betEndTime <= Date.now()) {
    return false
  }

  return betStartTime > Date.now()
}

/** ลำดับกลุ่มบนหน้า lobby/listall: เปิดรับ → ยังไม่เปิด → ปิดรับแทง → ยกเลิก */
export function getLottoRoundDisplayRank(
  round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
): number {
  if (isRoundCancelled(round)) return 3
  if (isRoundNotYetOpen(round)) return 1
  return canRoundAcceptBet(round) ? 0 : 2
}

/** เรียงรอบหวยบนหน้า /lotto — เปิดรับเรียงตามเวลาปิดรับ (เร็ว → ช้า), ปิดแล้วไปท้าย */
export function compareRoundsForLottoDisplay(
  a: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
  b: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
): number {
  const rankA = getLottoRoundDisplayRank(a)
  const rankB = getLottoRoundDisplayRank(b)
  if (rankA !== rankB) return rankA - rankB

  if (rankA === 0) {
    const endA = new Date(a.betEndTime).getTime()
    const endB = new Date(b.betEndTime).getTime()
    if (Number.isFinite(endA) && Number.isFinite(endB) && endA !== endB) {
      return endA - endB
    }
  } else if (rankA === 1) {
    const startA = new Date(a.betStartTime).getTime()
    const startB = new Date(b.betStartTime).getTime()
    if (Number.isFinite(startA) && Number.isFinite(startB) && startA !== startB) {
      return startA - startB
    }
  } else if (rankA === 2 || rankA === 3) {
    const endA = new Date(a.betEndTime).getTime()
    const endB = new Date(b.betEndTime).getTime()
    if (Number.isFinite(endA) && Number.isFinite(endB) && endA !== endB) {
      return endA - endB
    }
  }

  return 0
}

export function sortRoundsForLottoDisplay<T extends Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>>(
  rounds: T[],
): T[] {
  return [...rounds].sort(compareRoundsForLottoDisplay)
}

/** Bet slip item (รายการแทงในโพย) */
export interface BetSlipItem {
  prizeTypeCode: string
  prizeTypeName?: string
  number: string
  amount: number
  lockedPayoutRate?: number
  lockedDiscount?: number
  effectiveAmount?: number
  potentialPayout?: number
  status?: string
  [key: string]: unknown
}

/** Bet slip (โพย) from API */
export interface BetSlip {
  _id: string
  slipId?: string
  tenantId?: string
  userId?: string
  roundId?: string
  huayId?: string
  huayName?: string
  roundNumber?: string
  packageCode?: string
  packageName?: string
  betItems: BetSlipItem[]
  totalAmount?: number
  totalEffectiveAmount?: number
  totalPotentialPayout?: number
  totalWinAmount?: number
  totalDiscount?: number
  refundAmount?: number
  refundedAt?: string | null
  status?: string
  isSettled?: boolean
  createdAt?: string
  updatedAt?: string
  customerCancelWindowMinutes?: number
  [key: string]: unknown
}

export const CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES = 180
const CUSTOMER_SLIP_CANCEL_RULE_CODE = 'RULE_REFUND_SLIP_TIME'
let customerSlipCancelWindowCache = CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
let customerSlipCancelWindowPromise: Promise<number> | null = null
const TENANT_RULES_CACHE_TTL_MS = 30_000
const tenantRulesCache = new Map<string, { at: number; data: TenantRuleContentItem[] }>()
const tenantRulesPromiseCache = new Map<string, Promise<TenantRuleContentItem[]>>()

export function formatCustomerSlipCancelWindowText(minutes: number): string {
  const normalizedMinutes = Math.max(1, Math.round(minutes || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES))
  const wholeHours = normalizedMinutes / 60
  if (Number.isInteger(wholeHours)) {
    return `${wholeHours} ชม. หรือ ${normalizedMinutes} นาที`
  }
  return `${normalizedMinutes} นาที`
}

export interface TenantRuleContentItem {
  _id?: string
  code: string
  title: string
  content: string
  contentType?: 'text' | 'html' | 'markdown'
  category?: string
  isActive?: boolean
  displayOrder?: number
  priority?: number
  selectionMode?: 'all' | 'type' | 'huay'
  applyToAllHuayTypes?: boolean
  applyToAllHuays?: boolean
  relatedHuayTypes?: Array<string | { _id?: string; code?: string; name?: string; nameTh?: string }>
  relatedHuays?: Array<string | { _id?: string; code?: string; name?: string; nameTh?: string }>
  effectiveFrom?: string
  effectiveTo?: string | null
}

export interface TenantRuleScope {
  tenantId?: string
  huayId?: string
  huayName?: string
  huayTypeId?: string
  huayTypeName?: string
}

export interface TenantRulesGuideContent {
  rules: TenantRuleContentItem[]
  guides: TenantRuleContentItem[]
}

function extractScopedIds(items?: Array<string | { _id?: string }>): string[] {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && typeof item._id === 'string') return item._id
      return ''
    })
    .filter(Boolean)
}

function isRuleEffectiveNow(rule: TenantRuleContentItem): boolean {
  const now = Date.now()
  const effectiveFrom = rule.effectiveFrom ? new Date(rule.effectiveFrom).getTime() : null
  const effectiveTo = rule.effectiveTo ? new Date(rule.effectiveTo).getTime() : null

  if (effectiveFrom && !Number.isNaN(effectiveFrom) && effectiveFrom > now) return false
  if (effectiveTo && !Number.isNaN(effectiveTo) && effectiveTo < now) return false
  return rule.isActive !== false
}

function isRuleApplicableToScope(rule: TenantRuleContentItem, scope: TenantRuleScope): boolean {
  if (!isRuleEffectiveNow(rule)) return false

  const relatedHuayTypeIds = extractScopedIds(rule.relatedHuayTypes)
  const relatedHuayIds = extractScopedIds(rule.relatedHuays)
  const hasHuayTypeScope = relatedHuayTypeIds.length > 0 || rule.applyToAllHuayTypes === true
  const hasHuayScope = relatedHuayIds.length > 0 || rule.applyToAllHuays === true

  if (rule.selectionMode === 'huay' || hasHuayScope) {
    if (!scope.huayId) return rule.applyToAllHuays === true
    return rule.applyToAllHuays === true || relatedHuayIds.includes(scope.huayId)
  }

  if (rule.selectionMode === 'type' || hasHuayTypeScope) {
    if (!scope.huayTypeId) return rule.applyToAllHuayTypes === true
    return rule.applyToAllHuayTypes === true || relatedHuayTypeIds.includes(scope.huayTypeId)
  }

  return true
}

function sortTenantRuleItems(items: TenantRuleContentItem[]): TenantRuleContentItem[] {
  return [...items].sort((left, right) => {
    const leftPriority = Number(left.priority || 0)
    const rightPriority = Number(right.priority || 0)
    if (leftPriority !== rightPriority) return rightPriority - leftPriority

    const leftDisplayOrder = Number(left.displayOrder || 0)
    const rightDisplayOrder = Number(right.displayOrder || 0)
    if (leftDisplayOrder !== rightDisplayOrder) return leftDisplayOrder - rightDisplayOrder

    return String(left.title || left.code || '').localeCompare(String(right.title || right.code || ''))
  })
}

export interface UserSlipsResponse {
  success: boolean
  data: { slips: BetSlip[]; total: number; customerCancelWindowMinutes?: number }
  error?: string
}

function getTodayBangkokRange(): { todayStartBkk: Date; todayEndBkk: Date } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = formatter.formatToParts(new Date())
  const y = parts.find((p) => p.type === 'year')!.value
  const m = parts.find((p) => p.type === 'month')!.value
  const d = parts.find((p) => p.type === 'day')!.value
  const todayStartBkk = new Date(`${y}-${m}-${d}T00:00:00+07:00`)
  const todayEndBkk = new Date(`${y}-${m}-${d}T23:59:59.999+07:00`)
  return { todayStartBkk, todayEndBkk }
}

const isDev = (import.meta.env as { DEV?: boolean }).DEV === true
const ROUNDS_CACHE_TTL_MS = 15_000
let roundsCache: { data: LotteryRound[]; at: number } | null = null

const SHARED_HUAYS_CACHE_TTL_MS = 5 * 60_000
let sharedHuaysCache: { data: SharedHuayLite[]; at: number } | null = null

interface SharedHuayLite {
  _id?: string
  huayId?: string
  code?: string
  name?: string
}

class LotteryService {
  private baseUrl: string
  private tenantId: string

  constructor() {
    this.baseUrl =
      (import.meta.env as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL || 'https://gw.api-luck66.com'
    this.tenantId =
      getRuntimeTenantIdSync()
  }

  /** Called by App.tsx once the tenant is resolved from domain-mappings */
  setTenantId(id: string) {
    if (id && id !== this.tenantId) {
      this.tenantId = id
    }
  }

  /** Tenant ID to use in URLs — never empty (avoids /tenant//... and 400/404) */
  private getEffectiveTenantId(): string {
    const fallback =
      getRuntimeTenantIdSync()
    return this.tenantId && this.tenantId.trim() ? this.tenantId : fallback
  }

  async getCustomerSlipCancelWindowMinutes(): Promise<number> {
    if (customerSlipCancelWindowPromise) {
      return customerSlipCancelWindowPromise
    }

    const authToken = localStorage.getItem('accessToken')
    const tid = this.getEffectiveTenantId()
    const url = `${this.baseUrl}/api/v1/lottery/tenant/${tid}/rules-and-regulations/${CUSTOMER_SLIP_CANCEL_RULE_CODE}`

    customerSlipCancelWindowPromise = fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
        'X-Tenant-ID': tid,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = (await response.json().catch(() => ({}))) as {
          data?: { settings?: { slipCancelWindowMinutes?: number } }
        }
        const minutes = Number(result?.data?.settings?.slipCancelWindowMinutes)
        if (Number.isFinite(minutes) && minutes > 0) {
          customerSlipCancelWindowCache = minutes
        }
        return customerSlipCancelWindowCache
      })
      .catch(() => customerSlipCancelWindowCache)
      .finally(() => {
        customerSlipCancelWindowPromise = null
      })

    return customerSlipCancelWindowPromise
  }

  async getTenantRulesAndGuides(scope: TenantRuleScope = {}): Promise<TenantRulesGuideContent> {
    const tenantId = scope.tenantId || this.getEffectiveTenantId()
    const huayTypeId = String(scope.huayTypeId || '').trim()
    const cacheKey = `${tenantId}:${huayTypeId || 'all'}`
    const cached = tenantRulesCache.get(cacheKey)

    let rulesPromise: Promise<TenantRuleContentItem[]>
    if (cached && Date.now() - cached.at < TENANT_RULES_CACHE_TTL_MS) {
      rulesPromise = Promise.resolve(cached.data)
    } else if (tenantRulesPromiseCache.has(cacheKey)) {
      rulesPromise = tenantRulesPromiseCache.get(cacheKey)!
    } else {
      const authToken = localStorage.getItem('accessToken')
      const params = new URLSearchParams()
      params.set('isActive', 'true')
      if (huayTypeId) params.set('huayTypeId', huayTypeId)
      const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rules-and-regulations?${params.toString()}`

      rulesPromise = fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
          'X-Tenant-ID': tenantId,
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const result = (await response.json().catch(() => ({}))) as { data?: TenantRuleContentItem[] }
          const data = Array.isArray(result?.data) ? result.data : []
          tenantRulesCache.set(cacheKey, { at: Date.now(), data })
          return data
        })
        .catch(() => [])
        .finally(() => {
          tenantRulesPromiseCache.delete(cacheKey)
        })

      tenantRulesPromiseCache.set(cacheKey, rulesPromise)
    }

    const allItems = await rulesPromise
    const applicableItems = sortTenantRuleItems(
      allItems.filter((item) => isRuleApplicableToScope(item, scope))
    )

    return {
      rules: applicableItems.filter((item) => String(item.code || '').startsWith('RULE_')),
      guides: applicableItems.filter((item) => String(item.code || '').startsWith('GUIDE_')),
    }
  }

  async getTodayRounds(cacheBust = false): Promise<LotteryRound[]> {
    if (isDev && !cacheBust && roundsCache && Date.now() - roundsCache.at < ROUNDS_CACHE_TTL_MS) {
      return roundsCache.data
    }
    const query = `limit=1000&forToday=1&timezone=Asia/Bangkok${cacheBust ? `&_t=${Date.now()}` : ''}`
    const tid = this.getEffectiveTenantId()
    // ใช้ Public API (ไม่ต้องส่ง token) — ทำงานได้ทั้ง user ปกติและ View As เหมือนกัน
    const publicUrl = `${this.baseUrl}/api/v1/lottery/public/tenant/${tid}/rounds?${query}`
    try {
      const response = await fetch(publicUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tid,
        },
        ...(cacheBust && { cache: 'no-store' as RequestCache }),
      })

      if (!response.ok) {
        let serverMessage = ''
        try {
          const errBody = await response.json().catch(() => ({}))
          serverMessage = (errBody as { error?: string; message?: string }).error
            || (errBody as { error?: string; message?: string }).message
            || ''
        } catch {
          // ignore
        }
        if (serverMessage) {
          console.error('[lotteryService] Rounds API error:', response.status, serverMessage)
        }
        throw new Error(serverMessage ? `Rounds API: ${serverMessage}` : `HTTP error! status: ${response.status}`)
      }

      const result: LotteryRoundsResponse = await response.json()

      if (result.success && result.data) {
        const { todayStartBkk, todayEndBkk } = getTodayBangkokRange()

        const filtered = result.data.filter((round) => {
          const betStart = new Date(round.betStartTime)
          const betEnd = new Date(round.betEndTime)
          const overlapsToday = betEnd > todayStartBkk && betStart < todayEndBkk
          if (!overlapsToday) return false
          const betEndIsMidnightBkk =
            Math.abs(betEnd.getTime() - todayStartBkk.getTime()) < 60000
          const isYesterdayLastRound =
            betEndIsMidnightBkk && betStart.getTime() < todayStartBkk.getTime()
          return !isYesterdayLastRound
        })

        if (isDev) roundsCache = { data: filtered, at: Date.now() }
        return filtered
      }

      return []
    } catch (error) {
      console.error('Error fetching today rounds:', error)
      return []
    }
  }

  /**
   * โหลด catalog หวยทั้งหมดจาก shared resources (public optionalAuth) — ใช้สำหรับ
   * map `huayCode` → ชื่อภาษาไทย (`Huay.name`) ในหน้า /live-tv เพื่อกัน fallback
   * เป็นชื่ออังกฤษจาก formatHuayKeyAsTitle
   */
  async getSharedHuayNameMap(): Promise<Record<string, string>> {
    let huays = sharedHuaysCache && Date.now() - sharedHuaysCache.at < SHARED_HUAYS_CACHE_TTL_MS
      ? sharedHuaysCache.data
      : null

    if (!huays) {
      const url = `${this.baseUrl}/api/v1/lottery/shared/huays?isActive=true`
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
          console.error('[lotteryService] getSharedHuayNameMap HTTP', response.status, url)
          return {}
        }
        const json = await response.json().catch(() => null) as
          | { success?: boolean; data?: SharedHuayLite[] }
          | null
        const list = Array.isArray(json?.data) ? json!.data! : []
        sharedHuaysCache = { data: list, at: Date.now() }
        huays = list
      } catch (error) {
        console.error('[lotteryService] getSharedHuayNameMap error', error)
        return {}
      }
    }

    const map: Record<string, string> = {}
    for (const huay of huays) {
      const name = (huay?.name || '').trim()
      if (!name) continue
      const code = (huay?.code || '').trim()
      if (code) {
        const upper = code.toUpperCase()
        if (!map[upper]) map[upper] = name
        if (!map[code]) map[code] = name
      }
      const id = (huay?._id || '').trim()
      if (id && !map[id]) map[id] = name
      const huayId = (huay?.huayId || '').trim()
      if (huayId && !map[huayId]) map[huayId] = name
    }
    return map
  }

  /**
   * Get tenant huay types — Public API (ไม่ต้อง login) ตรง user-classic ใช้กรองรอบตามประเภทหวย
   */
  async getTenantHuayTypes(options?: { limit?: number }): Promise<PublicHuayType[]> {
    const limit = options?.limit ?? 100
    const tid = this.getEffectiveTenantId()
    const url = `${this.baseUrl}/api/v1/lottery/public/tenant/${tid}/huay-types?limit=${limit}`
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tid },
    })
    if (!response.ok) {
      console.error('[lotteryService] getTenantHuayTypes HTTP', response.status, url)
      return []
    }
    const result = (await response.json()) as { success?: boolean; data?: unknown }
    if (!result.success || !result.data) return []
    const raw = result.data
    const list = Array.isArray(raw)
      ? raw
      : (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)
          ? (raw as { data: unknown[] }).data
          : [])
    return list.map((ht: Record<string, unknown>) => ({
      _id: String(ht._id ?? ht.huayTypeId ?? ''),
      huayTypeId: ht.huayTypeId != null ? String(ht.huayTypeId) : ht._id != null ? String(ht._id) : undefined,
      name: String(ht.name ?? ''),
      code: ht.code != null ? String(ht.code) : undefined,
      imageUrl: ht.imageUrl != null ? String(ht.imageUrl) : null,
      imageURL: ht.imageURL != null ? String(ht.imageURL) : null,
      configuration:
        ht.configuration && typeof ht.configuration === 'object'
          ? { isActive: (ht.configuration as { isActive?: boolean }).isActive }
          : undefined,
    }))
  }

  /**
   * Get lottery rounds — Public API เท่านั้น (ไม่ต้อง login) ตรง user-classic
   * - ดึงแค่วันต่อวัน: ส่ง dateFrom, dateTo = วันที่เลือก (เช่น 2025-02-05), limit 1000
   * - ดึง round เดียว: ส่ง roundId, limit=1
   */
  async getLotteryRounds(options?: {
    page?: number
    limit?: number
    dateFrom?: string
    dateTo?: string
    roundId?: string
    timezone?: string
  }): Promise<LotteryRound[]> {
    const params = new URLSearchParams()
    params.set('page', String(options?.page ?? 1))
    params.set('limit', String(options?.limit ?? PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT))
    if (options?.dateFrom) params.set('dateFrom', options.dateFrom)
    if (options?.dateTo) params.set('dateTo', options.dateTo)
    if (options?.roundId) params.set('roundId', options.roundId)
    if (options?.timezone) params.set('timezone', options.timezone)
    const tid = this.getEffectiveTenantId()
    const pathWithLottery = `/api/v1/lottery/public/tenant/${tid}/lottery-rounds?${params}`
    const pathWithoutLottery = `/api/v1/public/tenant/${tid}/lottery-rounds?${params}`
    const headers = { 'Content-Type': 'application/json', 'X-Tenant-ID': tid }
    const tryFetch = async (path: string) => fetch(`${this.baseUrl}${path}`, { method: 'GET', headers })
    let response = await tryFetch(pathWithLottery)
    if (response.status === 404 && pathWithLottery !== pathWithoutLottery) {
      response = await tryFetch(pathWithoutLottery)
    }
    if (!response.ok) {
      const errText = await response.text()
      console.error('[lotteryService] getLotteryRounds HTTP', response.status, this.baseUrl + pathWithLottery, errText.slice(0, 200))
      const msg =
        response.status === 502
          ? '502 Bad Gateway — Lottery Service ไม่ตอบหรือ Load Balancer ชี้ผิด'
          : response.status === 503
            ? '503 Service Unavailable — Backend ยังไม่พร้อม'
            : response.status === 404
              ? '404 Not Found — path API ไม่มีใน Backend'
              : `API error ${response.status}: ${response.statusText}`
      throw new Error(msg)
    }
    const result = (await response.json()) as { success?: boolean; data?: unknown }
    if (!result.success) {
      console.error('[lotteryService] getLotteryRounds success=false', result)
      return []
    }
    const raw = result.data
    const list = Array.isArray(raw)
      ? raw
      : (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)
          ? (raw as { data: unknown[] }).data
          : [])
    return (list as LotteryRound[]).slice(0, options?.limit ?? PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT)
  }

  async getRoundById(roundId: string): Promise<LotteryRound | null> {
    const list = await this.getLotteryRounds({ roundId, limit: 1 })
    return list[0] ?? null
  }

  groupRoundsByType(rounds: LotteryRound[]): Record<string, LotteryRound[]> {
    return rounds.reduce(
      (acc, round) => {
        const typeName = round.huayTypeName || 'อื่นๆ'
        if (!acc[typeName]) acc[typeName] = []
        acc[typeName].push(round)
        return acc
      },
      {} as Record<string, LotteryRound[]>
    )
  }

  sortRoundsForLottoDisplay(rounds: LotteryRound[]): LotteryRound[] {
    return [...rounds].sort(compareRoundsForLottoDisplay)
  }

  getTimeRemaining(betEndTime: string): {
    hours: number
    minutes: number
    seconds: number
    isExpired: boolean
  } {
    const now = new Date().getTime()
    const endTime = new Date(betEndTime).getTime()
    const diff = endTime - now

    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    return { hours, minutes, seconds, isExpired: false }
  }

  formatTimeRemaining(betEndTime: string): string {
    const { hours, minutes, seconds, isExpired } = this.getTimeRemaining(betEndTime)

    if (isExpired) return 'ปิดรับแทง'

    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return `${days} วัน ${hours % 24}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  /**
   * Place bet (ส่งโพย) — ตรงกับ user-classic lotteryService.placeBet
   */
  async placeBet(
    userId: string,
    roundId: string,
    packageCode: string | null,
    betItems: Array<{ prizeTypeCode: string; number: string; amount: number }>,
    source: string = 'web',
    username?: string
  ): Promise<{ success: boolean; data?: { slipId?: string }; message?: string }> {
    try {
      const authToken = localStorage.getItem('accessToken')
      const url = `${this.baseUrl}/api/v1/lottery/tenant/${this.tenantId}/bet`
      const body: Record<string, unknown> = {
        userId,
        roundId,
        packageCode,
        betItems,
        source,
        ipAddress: null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }
      if (username != null && username !== '') body.username = username

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
          'X-Tenant-ID': this.tenantId,
        },
        body: JSON.stringify(body),
      })

      const responseBody = await parseResponseBody(response)

      if (!response.ok) {
        const message = resolvePlaceBetErrorMessage(response, responseBody)
        const errorPayload =
          responseBody.data && typeof responseBody.data === 'object'
            ? {
                ...(responseBody.data as Record<string, unknown>),
                message,
              }
            : {
                message,
                status: response.status,
                rawText: responseBody.text,
              }

        const apiError = new Error(message) as Error & {
          response?: { data?: unknown }
          details?: unknown
        }
        apiError.response = { data: errorPayload }
        apiError.details = errorPayload
        throw apiError
      }

      if (!responseBody.data || typeof responseBody.data !== 'object') {
        throw new Error('รูปแบบข้อมูลตอบกลับจากระบบไม่ถูกต้อง')
      }

      return responseBody.data as { success: boolean; data?: { slipId?: string }; message?: string }
    } catch (error) {
      console.error('Error placing bet:', error)
      throw error
    }
  }

  /**
   * Get user bet slips (โพยที่ซื้อ)
   */
  async getUserSlips(
    userId: string,
    options?: {
      limit?: number
      skip?: number
      status?: string
      roundId?: string
      isSettled?: boolean
      startDate?: string
      endDate?: string
      summary?: boolean
    }
  ): Promise<{ slips: BetSlip[]; total: number }> {
    const authToken = localStorage.getItem('accessToken')
    const params = new URLSearchParams()
    if (options?.limit != null) params.set('limit', String(options.limit))
    if (options?.skip != null) params.set('skip', String(options.skip))
    if (options?.status) params.set('status', options.status)
    if (options?.roundId) params.set('roundId', options.roundId)
    if (options?.isSettled !== undefined) params.set('isSettled', String(options.isSettled))
    if (options?.startDate) params.set('startDate', options.startDate)
    if (options?.endDate) params.set('endDate', options.endDate)
    if (options?.summary !== false) params.set('summary', 'true')
    const qs = params.toString()
    const url = `${this.baseUrl}/api/v1/lottery/tenant/${this.tenantId}/bet/user/${encodeURIComponent(userId)}${qs ? `?${qs}` : ''}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken || ''}`,
      'X-Tenant-ID': this.tenantId,
      'X-Timeout': '30000',
    }

    let lastStatus = 0
    const maxAttempts = SLIP_FETCH_RETRY_DELAYS_MS.length + 1

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const response = await fetch(url, { method: 'GET', headers })

        if (!response.ok) {
          lastStatus = response.status
          if (isRetryableSlipFetchStatus(response.status) && attempt < maxAttempts - 1) {
            await delayMs(SLIP_FETCH_RETRY_DELAYS_MS[attempt] ?? 1800)
            continue
          }
          throw new Error(resolveSlipFetchErrorMessage(response.status))
        }

        const result: UserSlipsResponse = await response.json()
        if (result.success && result.data) {
          const customerCancelWindowMinutes = Number(result.data.customerCancelWindowMinutes)
          const slips = (result.data.slips || []).map((slip) => ({
            ...slip,
            customerCancelWindowMinutes:
              Number.isFinite(customerCancelWindowMinutes) && customerCancelWindowMinutes > 0
                ? customerCancelWindowMinutes
                : Number(slip.customerCancelWindowMinutes) || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
          }))
          return {
            slips,
            total: result.data.total ?? slips.length,
          }
        }
        return { slips: [], total: 0 }
      } catch (error) {
        const isNetworkError =
          error instanceof TypeError ||
          (error instanceof Error &&
            (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')))

        if ((isNetworkError || (lastStatus && isRetryableSlipFetchStatus(lastStatus))) && attempt < maxAttempts - 1) {
          await delayMs(SLIP_FETCH_RETRY_DELAYS_MS[attempt] ?? 1800)
          continue
        }

        if (error instanceof Error) {
          throw error
        }
        throw new Error('ไม่สามารถเชื่อมต่อระบบโพยหวยได้ กรุณาลองใหม่')
      }
    }

    throw new Error(resolveSlipFetchErrorMessage(lastStatus || 503))
  }

  /**
   * Get single bet slip by slipId or _id (ตรง user-classic getSlip)
   * GET /api/v1/lottery/tenant/:tenantId/bet/slip/:slipId
   */
  async getSlip(slipId: string): Promise<BetSlip | null> {
    try {
      const authToken = localStorage.getItem('accessToken')
      const url = `${this.baseUrl}/api/v1/lottery/tenant/${this.tenantId}/bet/slip/${encodeURIComponent(slipId)}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
          'X-Tenant-ID': this.tenantId,
        },
      })
      if (!response.ok) {
        if (response.status === 404) return null
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const result = (await response.json()) as { success?: boolean; data?: BetSlip }
      if (result.success && result.data) {
        return {
          ...result.data,
          customerCancelWindowMinutes: Number(result.data.customerCancelWindowMinutes) || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
        }
      }
      return null
    } catch (error) {
      console.error('Error fetching slip:', error)
      return null
    }
  }

  async cancelSlip(slipId: string, reason = 'User cancelled'): Promise<{ success: boolean; message?: string; data?: unknown }> {
    try {
      const authToken = localStorage.getItem('accessToken')
      const url = `${this.baseUrl}/api/v1/lottery/tenant/${this.tenantId}/bet/slip/${encodeURIComponent(slipId)}/cancel`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
          'X-Tenant-ID': this.tenantId,
        },
        body: JSON.stringify({ reason }),
      })

      const result = (await response.json().catch(() => ({}))) as { success?: boolean; message?: string; data?: unknown }
      if (!response.ok || !result.success) {
        return {
          success: false,
          message: result.message || `HTTP error! status: ${response.status}`,
        }
      }

      return { success: true, message: result.message, data: result.data }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Cancel slip failed',
      }
    }
  }
}

/** รูปตามประเภทหวย (สำหรับการ์ดจาก API) */
export function getRoundImage(round: LotteryRound): string {
  if (round.huayImageURL) return round.huayImageURL
  const name = (round.huayTypeName || '') + (round.huayName || '')
  if (name.includes('รัฐบาลไทย') && !name.includes('ออมสิน') && !name.includes('ธ.ก.ส.'))
    return '/img/flag/th.png'
  if (name.includes('ธ.ก.ส.') || name.includes('ธกส')) return '/img/flag/egov.png'
  if (name.includes('ออมสิน')) return '/img/flag/gsb.png'
  return '/img/logo-t.png'
}

export const lotteryService = new LotteryService()
